import React, { useState, useRef, useEffect } from "react";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import Forward10Icon from "@mui/icons-material/Forward10";
import Replay10Icon from "@mui/icons-material/Replay10";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import {
  Box,
  IconButton,
  Slider,
  Menu,
  MenuItem,
  Typography,
  CircularProgress,
  LinearProgress
} from "@mui/material";

const AudioPlayer = ({
  src,
  styles,
  setIsAudioFinished,
  isSliderNeeded = true,
  coverImage = null
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isBuffering, setIsBuffering] = useState(false);
  const [buffered, setBuffered] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [speedMenuAnchor, setSpeedMenuAnchor] = useState(null);
  const [volumeMenuAnchor, setVolumeMenuAnchor] = useState(null);
  const [volume, setVolume] = useState(1);
  const [error, setError] = useState(false);
  const audioRef = useRef(null);
  const playPromiseRef = useRef(null);
  const seekingTimeoutRef = useRef(null);

  const speedOptions = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

  const primaryColor = "#368AD0";
  const lightGrayColor = styles?.lightGreyColor || "#94A3B8";

  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds) || timeInSeconds === Infinity) return "00:00";
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  // Calculate buffered percentage
  const getBufferedPercentage = () => {
    const audio = audioRef.current;
    if (audio && audio.buffered.length > 0 && duration > 0) {
      const bufferedEnd = audio.buffered.end(audio.buffered.length - 1);
      return (bufferedEnd / duration) * 100;
    }
    return 0;
  };

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio || isLoading || error) return;

    try {
      if (isPlaying) {
        if (playPromiseRef.current) {
          await playPromiseRef.current;
        }
        audio.pause();
        setIsPlaying(false);
        playPromiseRef.current = null;
      } else {
        setIsBuffering(true);
        playPromiseRef.current = audio.play();
        await playPromiseRef.current;
        setIsPlaying(true);
        setIsBuffering(false);
        playPromiseRef.current = null;
      }
    } catch (err) {
      console.error("Error toggling playback:", err);
      setIsPlaying(false);
      setIsBuffering(false);
      playPromiseRef.current = null;
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (event, newValue) => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = newValue;
      setVolume(newValue);
      if (newValue === 0) {
        setIsMuted(true);
        audio.muted = true;
      } else if (isMuted) {
        setIsMuted(false);
        audio.muted = false;
      }
    }
  };

  const handleVolumeMenuOpen = (event) => {
    setVolumeMenuAnchor(event.currentTarget);
  };

  const handleVolumeMenuClose = () => {
    setVolumeMenuAnchor(null);
  };

  const seekForward = () => {
    const audio = audioRef.current;
    if (audio && !isLoading && duration) {
      const newTime = Math.min(audio.currentTime + 10, duration);
      audio.currentTime = newTime;
      setCurrentTime(newTime);
      setIsBuffering(true);

      if (seekingTimeoutRef.current) {
        clearTimeout(seekingTimeoutRef.current);
      }
      seekingTimeoutRef.current = setTimeout(() => {
        setIsBuffering(false);
      }, 1000);
    }
  };

  const seekBackward = () => {
    const audio = audioRef.current;
    if (audio && !isLoading && duration) {
      const newTime = Math.max(audio.currentTime - 10, 0);
      audio.currentTime = newTime;
      setCurrentTime(newTime);
      setIsBuffering(true);

      if (seekingTimeoutRef.current) {
        clearTimeout(seekingTimeoutRef.current);
      }
      seekingTimeoutRef.current = setTimeout(() => {
        setIsBuffering(false);
      }, 1000);
    }
  };

  const handleSpeedChange = (speed) => {
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = speed;
      setPlaybackRate(speed);
      setSpeedMenuAnchor(null);
    }
  };

  const handleSpeedMenuOpen = (event) => {
    setSpeedMenuAnchor(event.currentTarget);
  };

  const handleSpeedMenuClose = () => {
    setSpeedMenuAnchor(null);
  };

  const handleSliderChange = (event, newValue) => {
    const audio = audioRef.current;
    if (audio && !isLoading && duration) {
      audio.currentTime = newValue;
      setCurrentTime(newValue);
      setIsBuffering(true);

      if (seekingTimeoutRef.current) {
        clearTimeout(seekingTimeoutRef.current);
      }
      seekingTimeoutRef.current = setTimeout(() => {
        setIsBuffering(false);
      }, 1000);
    }
  };

  useEffect(() => {
    const audio = audioRef.current;

    const handleLoadedMetadata = () => {
      if (
        audio.duration &&
        !isNaN(audio.duration) &&
        audio.duration !== Infinity
      ) {
        setDuration(audio.duration);
        setIsLoading(false);
        setError(false);
      }
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      setIsBuffering(false);
      setError(false);
    };

    const handleTimeUpdate = () => {
      if (!isNaN(audio.currentTime)) {
        setCurrentTime(audio.currentTime);
      }
    };

    const handleProgress = () => {
      setBuffered(getBufferedPercentage());
    };

    const handleWaiting = () => {
      setIsBuffering(true);
    };

    const handlePlaying = () => {
      setIsBuffering(false);
      setIsPlaying(true);
    };

    const handleSeeking = () => {
      setIsBuffering(true);
    };

    const handleSeeked = () => {
      setIsBuffering(false);
    };

    const handleEnded = () => {
      setCurrentTime(0);
      setIsPlaying(false);
      setIsBuffering(false);
      if (setIsAudioFinished) setIsAudioFinished(true);
    };

    const handleError = (e) => {
      console.error("Audio error:", e);
      setIsLoading(false);
      setIsBuffering(false);
      setIsPlaying(false);
      setError(true);
    };

    if (audio) {
      setIsLoading(true);
      setError(false);

      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      audio.addEventListener("canplay", handleCanPlay);
      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("progress", handleProgress);
      audio.addEventListener("waiting", handleWaiting);
      audio.addEventListener("playing", handlePlaying);
      audio.addEventListener("seeking", handleSeeking);
      audio.addEventListener("seeked", handleSeeked);
      audio.addEventListener("ended", handleEnded);
      audio.addEventListener("error", handleError);

      audio.playbackRate = playbackRate;
      audio.volume = volume;
      audio.muted = isMuted;

      // Force load the audio
      audio.load();
    }

    return () => {
      if (audio) {
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
        audio.removeEventListener("canplay", handleCanPlay);
        audio.removeEventListener("timeupdate", handleTimeUpdate);
        audio.removeEventListener("progress", handleProgress);
        audio.removeEventListener("waiting", handleWaiting);
        audio.removeEventListener("playing", handlePlaying);
        audio.removeEventListener("seeking", handleSeeking);
        audio.removeEventListener("seeked", handleSeeked);
        audio.removeEventListener("ended", handleEnded);
        audio.removeEventListener("error", handleError);
      }
      if (seekingTimeoutRef.current) {
        clearTimeout(seekingTimeoutRef.current);
      }
    };
  }, [src]);

  // Separate effect for volume and playback rate changes
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = volume;
      audio.muted = isMuted;
    }
  }, [volume, isMuted]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio && !isLoading) {
      audio.playbackRate = playbackRate;
    }
  }, [playbackRate, isLoading]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        background: styles.inputLabelColor,
        borderRadius: "10px",
        padding: "12px",
        position: "relative"
      }}
    >
      {/* Loading/Buffering Indicator */}
      {(isLoading || isBuffering) && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "2px",
            zIndex: 10
          }}
        >
          <LinearProgress
            sx={{
              height: "2px",
              borderRadius: "10px 10px 0 0",
              backgroundColor: "transparent",
              "& .MuiLinearProgress-bar": {
                backgroundColor: primaryColor
              }
            }}
          />
        </Box>
      )}

      {/* Error Message */}
      {error && (
        <Typography
          variant="caption"
          sx={{
            color: "#ef4444",
            textAlign: "center",
            mb: 1,
            fontSize: "0.7rem"
          }}
        >
          Failed to load audio
        </Typography>
      )}

      <Box display="flex" alignItems="center" width="100%">
        <IconButton
          sx={{
            p: 0.5,
            color: isLoading || error ? "#CCCCCC" : lightGrayColor,
            "&:hover": { color: isLoading || error ? "#CCCCCC" : primaryColor },
            transition: "all 0.2s ease"
          }}
          onClick={seekBackward}
          disabled={isLoading || !duration || error}
          size="small"
        >
          <Replay10Icon fontSize="small" />
        </IconButton>

        <IconButton
          sx={{
            padding: "6px",
            borderRadius: "50%",
            background: isLoading || error ? "#E2E8F0" : primaryColor,
            color: "#FFFFFF",
            mx: 0.5,
            position: "relative",
            "&:hover": {
              background: isLoading || error ? "#E2E8F0" : primaryColor,
              opacity: 0.9,
              transform: "scale(1.05)"
            },
            "&:disabled": {
              backgroundColor: "#E2E8F0",
              color: "#94A3B8"
            },
            transition: "all 0.2s ease"
          }}
          onClick={togglePlay}
          disabled={isLoading || !duration || error}
          size="small"
        >
          {isPlaying ? (
            <PauseIcon sx={{ fontSize: 18 }} />
          ) : (
            <PlayArrowIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>

        <IconButton
          sx={{
            p: 0.5,
            color: isLoading || error ? "#CCCCCC" : lightGrayColor,
            "&:hover": { color: isLoading || error ? "#CCCCCC" : primaryColor },
            transition: "all 0.2s ease"
          }}
          onClick={seekForward}
          disabled={isLoading || !duration || error}
          size="small"
        >
          <Forward10Icon fontSize="small" />
        </IconButton>

        {isSliderNeeded && (
          <>
            <Typography
              variant="caption"
              sx={{
                color: lightGrayColor,
                mx: 1,
                fontSize: "0.7rem",
                minWidth: "32px",
                fontWeight: 500
              }}
            >
              {formatTime(currentTime)}
            </Typography>

            <Box sx={{ flex: 1, position: "relative", mx: 0.5 }}>
              {/* Buffered progress */}
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  transform: "translateY(-50%)",
                  left: 0,
                  right: 0,
                  height: 3,
                  backgroundColor: "#FFF",
                  borderRadius: "1.5px",
                  overflow: "hidden"
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: `${buffered}%`,
                    backgroundColor: `${primaryColor}40`,
                    transition: "width 0.3s ease"
                  }}
                />
              </Box>

              {/* Current progress slider */}
              <Slider
                value={currentTime}
                max={duration || 1}
                onChange={handleSliderChange}
                disabled={isLoading || !duration || error}
                size="small"
                sx={{
                  padding: "3px !important",
                  color: primaryColor,
                  height: 6,
                  position: "relative",
                  zIndex: 2,
                  "& .MuiSlider-thumb": {
                    width: 10,
                    height: 10,
                    transition: "all 0.2s ease",
                    "&:hover, &.Mui-focusVisible": {
                      boxShadow: `0 0 0 8px ${primaryColor}30`,
                      width: 12,
                      height: 12
                    }
                  },
                  "& .MuiSlider-track": {
                    backgroundColor: primaryColor,
                    border: "none"
                  },
                  "& .MuiSlider-rail": {
                    backgroundColor: "transparent"
                  }
                }}
              />
            </Box>

            <Typography
              variant="caption"
              sx={{
                color: lightGrayColor,
                fontSize: "0.7rem",
                minWidth: "32px",
                textAlign: "right",
                fontWeight: 500
              }}
            >
              {isLoading ? "--:--" : formatTime(duration)}
            </Typography>
          </>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "4px 10px",
            borderRadius: "14px",
            bgcolor: primaryColor,
            ml: 2,
            mr: 1,
            cursor: isLoading || error ? "not-allowed" : "pointer",
            opacity: isLoading || error ? 0.5 : 1,
            transition: "all 0.2s ease",
            "&:hover": {
              opacity: isLoading || error ? 0.5 : 0.9,
              transform: isLoading || error ? "none" : "scale(1.05)"
            }
          }}
          onClick={isLoading || error ? undefined : handleSpeedMenuOpen}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: styles.color || "#FFFFFF",
              fontSize: "0.75rem"
            }}
          >
            {playbackRate}x
          </Typography>
        </Box>

        <Menu
          anchorEl={speedMenuAnchor}
          open={Boolean(speedMenuAnchor)}
          onClose={handleSpeedMenuClose}
          PaperProps={{
            sx: {
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
            }
          }}
        >
          {speedOptions.map((speed) => (
            <MenuItem
              key={speed}
              onClick={() => handleSpeedChange(speed)}
              selected={playbackRate === speed}
              sx={{
                fontWeight: playbackRate === speed ? 600 : 400,
                color: playbackRate === speed ? primaryColor : "inherit",
                py: 1,
                px: 2,
                minWidth: "90px",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: `${primaryColor}15`
                }
              }}
            >
              {speed}x
            </MenuItem>
          ))}
        </Menu>

        <IconButton
          sx={{
            p: 0.5,
            color: isLoading || error ? "#CCCCCC" : lightGrayColor,
            "&:hover": { color: isLoading || error ? "#CCCCCC" : primaryColor },
            transition: "all 0.2s ease"
          }}
          onClick={handleVolumeMenuOpen}
          disabled={isLoading || !duration || error}
          size="small"
        >
          {isMuted || volume === 0 ? (
            <VolumeOffIcon fontSize="small" />
          ) : (
            <VolumeUpIcon fontSize="small" />
          )}
        </IconButton>

        <Menu
          anchorEl={volumeMenuAnchor}
          open={Boolean(volumeMenuAnchor)}
          onClose={handleVolumeMenuClose}
          PaperProps={{
            sx: {
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              p: 1
            }
          }}
        >
          <Box
            sx={{
              width: 180,
              display: "flex",
              alignItems: "center",
              px: 2,
              py: 1
            }}
          >
            <VolumeOffIcon
              fontSize="small"
              sx={{ color: lightGrayColor, mr: 1.5, cursor: "pointer" }}
              onClick={() => handleVolumeChange(null, 0)}
            />
            <Slider
              value={isMuted ? 0 : volume}
              min={0}
              max={1}
              step={0.01}
              onChange={handleVolumeChange}
              sx={{
                padding: "3px !important",
                flexGrow: 1,
                color: primaryColor,
                "& .MuiSlider-thumb": {
                  width: 12,
                  height: 12,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    boxShadow: `0 0 0 8px ${primaryColor}30`
                  }
                },
                "& .MuiSlider-track": {
                  backgroundColor: primaryColor
                },
                "& .MuiSlider-rail": {
                  backgroundColor: "#E1F5E6"
                }
              }}
            />
            <VolumeUpIcon
              fontSize="small"
              sx={{ color: lightGrayColor, ml: 1.5, cursor: "pointer" }}
              onClick={() => handleVolumeChange(null, 1)}
            />
          </Box>
        </Menu>
      </Box>

      <audio ref={audioRef} src={src} preload="auto" crossOrigin="anonymous" />
    </Box>
  );
};

export default AudioPlayer;
